import { themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ChannelResource, DeploymentActionResource, DeploymentProcessRepository, GitRefResource, ProjectResource, ValidateGitRefV2Response, ParentEnvironmentV2Resource, LifecycleResource, Repository } from "@octopusdeploy/octopus-server-client";
import { HasGitPersistenceSettings, OctopusError, getParentEnvironments } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as _ from "lodash";
import { type ProjectContextProps } from "~/areas/projects/context";
import Chip from "~/components/Chips/Chip";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import type { ConvertedOverflowMenuItems } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import { Form } from "~/components/form/Form/Form";
import type { TagIndex } from "~/components/tagSets";
import * as tagSets from "~/components/tagSets";
import { CreateOrEditChannelForm } from "./CreateOrEditChannelForm";
import { useChannelForm } from "./useChannelForm";
export async function channelLookupDataLoader(repository: Repository, projectContext: Promise<ProjectContextProps>, ephemeralEnvironmentsAreEnabled: boolean): Promise<ChannelLookupData> {
    const tagIndex = tagSets.getTagIndex(["Tenant"]);
    const { state: { model: project, projectContextRepository, gitRefValidationError }, } = await projectContext;
    const deploymentActions = getDeploymentActionsWithErrorHandlingForGitProjects(project, projectContextRepository.DeploymentProcesses, gitRefValidationError);
    const parentEnvironments = getParentEnvironments(ephemeralEnvironmentsAreEnabled, repository, project);
    const lifecycles = await repository.Lifecycles.all();
    return {
        deploymentActions: await deploymentActions,
        lifecycles: await lifecycles,
        tagIndex: await tagIndex,
        parentEnvironments: await parentEnvironments,
    };
}
export interface ChannelLookupData {
    deploymentActions: DeploymentActionResource[] | OctopusError;
    lifecycles: LifecycleResource[];
    tagIndex: TagIndex;
    parentEnvironments: ParentEnvironmentV2Resource[];
}
type CreateOrEditChannelProps = {
    project: ProjectResource;
    gitRefValidationError: ValidateGitRefV2Response | undefined;
    gitRef: Readonly<GitRefResource> | undefined;
    lookupData: ChannelLookupData;
    channel: ChannelResource | "create new channel";
    pageTitle?: string;
    overflowMenuItems: ConvertedOverflowMenuItems;
};
export function CreateOrEditChannelPageContent({ channel, project, gitRefValidationError, gitRef, lookupData, pageTitle, overflowMenuItems }: CreateOrEditChannelProps) {
    const { lifecycles } = lookupData;
    const { status, doBusyTask } = useLegacyDoBusyTask();
    const { model, setModel, formProps, createSaveAction, getFieldError, lifecycleOptions } = useChannelForm(project, channel, lifecycles, "Save channel");
    const isCreatingNewChannel = channel === "create new channel";
    const title = pageTitle ?? model.Name;
    return (<PageContent header={{
            title,
            primaryAction: createSaveAction({ saveButtonLabel: "Save", saveButtonBusyLabel: "Saving" }),
            breadcrumbs: [{ label: "Channels", pageUrl: links.channelsPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }) }],
            chip: model.Type === "EphemeralEnvironment" ? (<Chip backgroundColor={themeTokens.color.button.background.tertiary.hover} labelColor={themeTokens.color.button.icon.secondary} noTooltip>
                            Ephemeral Environment
                        </Chip>) : undefined,
            overflowActions: overflowMenuItems.menuItems,
        }} legacyStatus={status}>
            {overflowMenuItems.dialogs}
            <Form {...formProps} expandAllOnMount={isCreatingNewChannel}>
                <TransitionAnimation>
                    <CreateOrEditChannelForm doBusyTask={doBusyTask} model={model} setModel={setModel} getFieldError={getFieldError} lifecycleOptions={lifecycleOptions} channel={channel} project={project} gitRefValidationError={gitRefValidationError} gitRef={gitRef} lookupData={lookupData}/>
                </TransitionAnimation>
            </Form>
        </PageContent>);
}
async function getDeploymentActionsWithErrorHandlingForGitProjects(project: ProjectResource, repository: DeploymentProcessRepository, gitRefValidationError: ValidateGitRefV2Response | undefined): Promise<DeploymentActionResource[] | OctopusError> {
    try {
        if (gitRefValidationError) {
            return [];
        }
        else {
            const process = await repository.getResolved();
            return _.flatMap(process.Steps, (step) => step.Actions);
        }
    }
    catch (exception) {
        if (HasGitPersistenceSettings(project.PersistenceSettings) && OctopusError.isOctopusError(exception)) {
            // We only want to catch the error if it's a Git project and an Octopus exception. Otherwise, error
            // as usual
            return exception;
        }
        else {
            throw exception;
        }
    }
}
